import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Filters } from '@aimsos/ui';

import {
  CATEGORY_OPTIONS,
  DEFAULT_SORT_DIRECTION,
  DEFAULT_SORT_FIELD,
  SORT_FIELD_OPTIONS,
  STATUS_OPTIONS,
} from '../constants/widgetLibraryFilters.js';
import { translateFilterOptions } from '../utils/translateFilterOptions.js';

/** Stable reference — `Filters` is `memo()`-wrapped; an inline object here would re-render it every time. */
const VIEW_TOGGLE_HIDDEN = { hidden: true };

/**
 * Library toolbar (CT-5186 / DS-2): the DS `Filters` bar, not a hand-rolled row
 * of selects. Two chip slots live on the bar; `TYPE` and `GOVERNED` live in the
 * "All filters" slideout, which the page owns via `onAllFiltersClick`.
 *
 * This component only translates the hook's state into `Filters` props — it
 * holds no filter state and does no narrowing of its own.
 */
function WidgetLibraryFilterBar({ filtersState, onAllFiltersClick }) {
  const { t } = useTranslation('dashboards');
  const {
    searchTerm,
    setSearchTerm,
    committed,
    setCategoryFromSlot,
    setStatusFromSlot,
    setTypeFromSlot,
    setGovernedFromSlot,
    setProfileFromSlot,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    clearAll,
    activeFilterCount,
  } = filtersState;

  const searchProps = useMemo(
    () => ({
      value: searchTerm,
      onChange: (event) => setSearchTerm(event?.target?.value ?? event ?? ''),
      placeholder: t('widgetLibrary.filters.searchPlaceholder'),
    }),
    [searchTerm, setSearchTerm, t],
  );

  const slots = useMemo(
    () => [
      {
        id: 'category',
        label: t('widgetLibrary.filters.category.label'),
        multiple: false,
        // `Filters` slots are array-valued; the page keeps this dimension as a
        // scalar, so it is adapted at the boundary (see useWidgetLibraryFilters).
        value: committed.category ? [committed.category] : [],
        onChange: setCategoryFromSlot,
        options: translateFilterOptions(CATEGORY_OPTIONS, t),
      },
      {
        id: 'status',
        label: t('widgetLibrary.filters.status.label'),
        multiple: false,
        value: committed.status ? [committed.status] : [],
        onChange: setStatusFromSlot,
        options: translateFilterOptions(STATUS_OPTIONS, t),
      },
      // Hidden slots: `maxFilters={2}` below keeps only Category/Status
      // rendered as bar chips (`Filters` slices its own `filters` prop to that
      // cap), but Type/Governed still ride along in the array so `Filters`'
      // internal "has an active filter slot" check — which scans the full
      // array, not just what's visible — sees them too. Without this, applying
      // Type or Governed from the slideout alone never showed the bar's
      // "Clear filters" link, even though a filter was genuinely active.
      {
        id: 'type',
        value: committed.type ? [committed.type] : [],
        onChange: setTypeFromSlot,
        options: [],
      },
      {
        id: 'governed',
        value: committed.governed ? [committed.governed] : [],
        onChange: setGovernedFromSlot,
        options: [],
      },
      {
        id: 'profile',
        value: committed.profile ? [committed.profile] : [],
        onChange: setProfileFromSlot,
        options: [],
      },
    ],
    [
      committed.category, committed.status, committed.type, committed.governed, committed.profile,
      setCategoryFromSlot, setStatusFromSlot, setTypeFromSlot, setGovernedFromSlot,
      setProfileFromSlot, t,
    ],
  );

  const sort = useMemo(
    () => ({
      label: t('widgetLibrary.filters.sort.label'),
      value: sortField,
      defaultValue: DEFAULT_SORT_FIELD,
      options: translateFilterOptions(SORT_FIELD_OPTIONS, t),
      onChange: setSortField,
      direction: sortDirection,
      defaultDirection: DEFAULT_SORT_DIRECTION,
      onDirectionChange: setSortDirection,
    }),
    [setSortDirection, setSortField, sortDirection, sortField, t],
  );

  const i18n = useMemo(
    () => ({
      // `Filters`' "All filters" button has no badge slot (@aimsos/ui 1.4.1), so
      // the active-dimension count rides in the label itself.
      allFilters: activeFilterCount
        ? t('widgetLibrary.filters.allFiltersWithCount', { count: activeFilterCount })
        : t('widgetLibrary.filters.allFilters'),
      clearFilters: t('widgetLibrary.filters.clearAll'),
    }),
    [activeFilterCount, t],
  );

  return (
    <Filters
      searchProps={searchProps}
      filters={slots}
      maxFilters={2}
      sort={sort}
      viewToggle={VIEW_TOGGLE_HIDDEN}
      onClearAll={clearAll}
      onAllFiltersClick={onAllFiltersClick}
      i18n={i18n}
    />
  );
}

WidgetLibraryFilterBar.propTypes = {
  filtersState: PropTypes.shape({
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    committed: PropTypes.shape({
      category: PropTypes.string,
      status: PropTypes.string,
      type: PropTypes.string,
      governed: PropTypes.string,
      profile: PropTypes.string,
    }).isRequired,
    setCategoryFromSlot: PropTypes.func.isRequired,
    setStatusFromSlot: PropTypes.func.isRequired,
    setTypeFromSlot: PropTypes.func.isRequired,
    setGovernedFromSlot: PropTypes.func.isRequired,
    setProfileFromSlot: PropTypes.func.isRequired,
    sortField: PropTypes.string.isRequired,
    sortDirection: PropTypes.string.isRequired,
    setSortField: PropTypes.func.isRequired,
    setSortDirection: PropTypes.func.isRequired,
    clearAll: PropTypes.func.isRequired,
    activeFilterCount: PropTypes.number.isRequired,
  }).isRequired,
  onAllFiltersClick: PropTypes.func,
};

export default WidgetLibraryFilterBar;
